import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import {
  IDENTIFICATION_ACTION,
  recordIdentificationAction,
} from "./identificationActionApi";

export default function FaceNotRecognized() {
  usePageMeta("Face Not Recognized");
  const navigate = useNavigate();
  const location = useLocation();
  const { capturedImage, reason, message, error, identificationId } = location.state || {};
  const notFoundRecordedRef = useRef(false);

  useEffect(() => {
    if (!identificationId || notFoundRecordedRef.current) return;
    notFoundRecordedRef.current = true;
    recordIdentificationAction(identificationId, {
      action: IDENTIFICATION_ACTION.NOT_FOUND,
    }).then((ok) => {
      if (!ok) notFoundRecordedRef.current = false;
    });
  }, [identificationId]);

  const handleNeedHelp = async () => {
    await recordIdentificationAction(identificationId, {
      action: IDENTIFICATION_ACTION.NEED_HELP,
      status: "I NEED HELP",
    });
    navigate("/checkin/help" + window.location.search);
  };

  // Determine the appropriate title and message based on the reason
  const getDisplayContent = () => {
    if (error) {
      return {
        title: "Face Recognition Error",
        subtitle: error
      };
    }

    switch (reason) {
      case "NO_PROFILE_PICTURE":
        return {
          title: "No Profile Picture Found",
          subtitle: message || "You need to upload a profile picture before using face recognition."
        };
      case "FACE_NOT_MATCHED":
        return {
          title: "We didn't recognize you from your photo",
          subtitle: "The face scan didn't match your stored profile picture."
        };
      default:
        return {
          title: "We didn't recognize you from your photo",
          subtitle: "Please try again or contact support if the issue persists."
        };
    }
  };

  const displayContent = getDisplayContent();

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-4">
        <h1 className="text-lg font-bold text-center mb-2 text-red-600">
          {displayContent.title}
        </h1>

        <p className="text-sm text-gray-600 text-center mb-4">
          {displayContent.subtitle}
        </p>

        {/* Scanned Photo Circle */}
        <div className="flex justify-center mb-4">
          <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-red-300">
            {capturedImage ? (
              <img 
                src={capturedImage} 
                alt="Captured face" 
                className="w-full h-full object-cover"
              />
            ) : (
              <svg
                className="w-20 h-20 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => navigate("/checkin/scan" + window.location.search)}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-sm hover:bg-[#b91900] transition-colors"
          >
            SCAN FACE AGAIN
          </button>

          <button
            onClick={() => navigate("/checkin/verify-phone" + window.location.search)}
            className="w-full bg-red-700 text-white py-3 px-4 rounded-lg font-semibold text-sm hover:bg-red-800 transition-colors"
          >
            IDENTIFY WITH MOBILE PHONE NUMBER
          </button>

          <button
            onClick={handleNeedHelp}
            className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-sm hover:bg-gray-400 transition-colors"
          >
            I NEED HELP
          </button>
        </div>
      </div>
    </div>
  );
}
